import React, { useContext, useMemo, useState } from 'react';
import logo from '../images/paznic-logo.png';
import { StyledLink } from './shared/styledLink';
import { UserLevel } from '../enums/userLevel';
import { Drawer, IconButton, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles, styled } from '@material-ui/styles';
import * as navIcons from '../images/navIcons';
import useReactRouter from 'use-react-router';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { OrgSiteContext } from './orgSiteContext';
import {
    NotificationsNone,
    ScheduleOutlined,
    ChevronLeft,
    ChevronRight,
    DescriptionOutlined,
    BusinessOutlined,
    AccountBalanceOutlined,
    LocationOnOutlined,
    Brightness4,
    Brightness7,
    Menu,
    HomeOutlined,
    PeopleOutlined,
    ViewModuleOutlined,
    BuildOutlined,
    AssignmentIndOutlined,
} from '@material-ui/icons';
import { navLinkUrl } from '../utils';

import { useAuth } from "../features/auth/context";

export const NavBar = () => {
    const { location } = useReactRouter();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(() => {
        const saved = localStorage.getItem('navBarIsOpen');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [isDark, setIsDark] = useState(() => {
        const saved = localStorage.getItem('navBarIsDark');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // On mobile the drawer is always "expanded" when open
    const effectiveIsOpen = isMobile ? true : isOpen;

    const classes = useStyles({ isOpen, isDark, isMobile });
    const theme = useTheme();
    const activeIconColor = isDark ? '#ffffff' : theme.palette.primary.light;

    const assignedOrgAndSite = useAuth();
    const orgSiteContext = useContext(OrgSiteContext);
    const dealerId = useMemo(
        () => orgSiteContext.dealerId || assignedOrgAndSite.dealerId,
        [assignedOrgAndSite.dealerId, orgSiteContext.dealerId]
    );
    const orgId = useMemo(
        () => orgSiteContext.orgId || assignedOrgAndSite.orgId,
        [assignedOrgAndSite.orgId, orgSiteContext.orgId]
    );
    const siteId = useMemo(
        () => orgId === null ? null : orgSiteContext.siteId || assignedOrgAndSite.siteId,
        [orgSiteContext.siteId, assignedOrgAndSite.siteId, orgId]
    );

    const toggleDrawer = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        localStorage.setItem('navBarIsOpen', JSON.stringify(newState));
    };

    const toggleDarkMode = () => {
        const newState = !isDark;
        setIsDark(newState);
        localStorage.setItem('navBarIsDark', JSON.stringify(newState));
    };

    const drawerInner = (
        <>
            <div className={classes.drawerHeader}>
                <div className={classes.logoWrapper}>
                    <img alt="" src={logo} className={classes.logo} />
                </div>
                {isMobile ? (
                    <IconButton onClick={() => setMobileOpen(false)} className={classes.closeButton}>
                        <ChevronLeft />
                    </IconButton>
                ) : (
                    <IconButton onClick={toggleDrawer} className={classes.menuButton}>
                        {isOpen ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>
                )}
            </div>
            <LinkList>
                <NavbarLink
                    altText="home page icon"
                    exact
                    iconSrc={{ active: navIcons.home, inactive: navIcons.homeInactive }}
                    iconOverride={{
                        active: <HomeOutlined style={{ color: activeIconColor }} />,
                        inactive: <HomeOutlined />,
                    }}
                    path={navLinkUrl('home', dealerId, orgId, siteId)}
                    linkText="Home"
                    isActive={location.pathname.indexOf('home') !== -1 || location.pathname === '/'}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="customers page icon"
                    iconSrc={{ active: navIcons.customersActive, inactive: navIcons.customers }}
                    iconOverride={{
                        active: <PeopleOutlined style={{ color: activeIconColor }} />,
                        inactive: <PeopleOutlined />,
                    }}
                    path={navLinkUrl('customers', dealerId, orgId, siteId)}
                    linkText="Customers"
                    isActive={location.pathname.indexOf('customers') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="boxes page icon"
                    iconSrc={{ active: navIcons.boxesActive, inactive: navIcons.boxes }}
                    iconOverride={{
                        active: <ViewModuleOutlined style={{ color: activeIconColor }} />,
                        inactive: <ViewModuleOutlined />,
                    }}
                    path={navLinkUrl('boxes', dealerId, orgId, siteId)}
                    linkText="Boxes"
                    isActive={location.pathname.indexOf('boxes') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="manual override page icon"
                    iconSrc={{ active: navIcons.wrenchActive, inactive: navIcons.wrench }}
                    iconOverride={{
                        active: <BuildOutlined style={{ color: activeIconColor }} />,
                        inactive: <BuildOutlined />,
                    }}
                    path={navLinkUrl('manual-override', dealerId, orgId, siteId)}
                    linkText="Manual Override"
                    authRoles={[
                        UserLevel.GlobalAdmin,
                        UserLevel.DealerAdmin,
                        UserLevel.OrgAdmin,
                        UserLevel.SiteAdmin,
                        UserLevel.SiteEmployee,
                    ]}
                    isActive={location.pathname.indexOf('manual-override') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="scheduled jobs page icon"
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    path={navLinkUrl('scheduled-jobs', dealerId, orgId, siteId)}
                    iconOverride={{
                        active: <ScheduleOutlined style={{ color: activeIconColor }} />,
                        inactive: <ScheduleOutlined />,
                    }}
                    linkText="Scheduled Jobs"
                    authRoles={[UserLevel.GlobalAdmin]}
                    isActive={location.pathname.indexOf('scheduled-jobs') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="employees page icon"
                    iconSrc={{ active: navIcons.customersActive, inactive: navIcons.customers }}
                    iconOverride={{
                        active: <AssignmentIndOutlined style={{ color: activeIconColor }} />,
                        inactive: <AssignmentIndOutlined />,
                    }}
                    path={navLinkUrl('employees', dealerId, orgId, siteId)}
                    linkText="Employees"
                    isActive={location.pathname.indexOf('employees') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="events page icon"
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    iconOverride={{
                        active: <DescriptionOutlined style={{ color: activeIconColor }} />,
                        inactive: <DescriptionOutlined />,
                    }}
                    path={navLinkUrl('reports', dealerId, orgId, siteId)}
                    linkText="Reports"
                    isActive={location.pathname.indexOf('reports') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="alarms page icon"
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    iconOverride={{
                        active: <NotificationsNone style={{ color: activeIconColor }} />,
                        inactive: <NotificationsNone />,
                    }}
                    path={navLinkUrl('alarms', dealerId, orgId, siteId)}
                    linkText="Alarms"
                    isActive={location.pathname.indexOf('alarms') !== -1}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="dealers page icon"
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    iconOverride={{
                        active: <BusinessOutlined style={{ color: activeIconColor }} />,
                        inactive: <BusinessOutlined />,
                    }}
                    path="/dealers"
                    linkText="Dealers"
                    authRoles={[UserLevel.GlobalAdmin]}
                    isActive={location.pathname === '/dealers'}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="orgs page icon"
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    iconOverride={{
                        active: <AccountBalanceOutlined style={{ color: activeIconColor }} />,
                        inactive: <AccountBalanceOutlined />,
                    }}
                    path={navLinkUrl("organizations", dealerId)}
                    linkText="Organizations"
                    authRoles={[
                        UserLevel.GlobalAdmin,
                        UserLevel.DealerAdmin,
                        UserLevel.DealerEmployee,
                    ]}
                    isActive={/organizations$/.test(location.pathname)}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
                <NavbarLink
                    altText="sites page icon"
                    exact
                    iconSrc={{ active: navIcons.reportActive, inactive: navIcons.report }}
                    iconOverride={{
                        active: <LocationOnOutlined style={{ color: activeIconColor }} />,
                        inactive: <LocationOnOutlined />,
                    }}
                    path={navLinkUrl('sites', dealerId, orgId, siteId)}
                    linkText="Sites"
                    isActive={/sites$/.test(location.pathname)}
                    isDrawerOpen={effectiveIsOpen}
                    isDark={isDark}
                />
            </LinkList>
            <DarkModeToggle isDark={isDark} isDrawerOpen={effectiveIsOpen} onToggle={toggleDarkMode} />
        </>
    );

    if (isMobile) {
        return (
            <>
                <IconButton
                    onClick={() => setMobileOpen(true)}
                    className={classes.hamburger}
                >
                    <Menu />
                </IconButton>
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    classes={{ paper: classes.drawerPaper }}
                    ModalProps={{ keepMounted: true }}
                >
                    {drawerInner}
                </Drawer>
            </>
        );
    }

    return (
        <Drawer
            classes={{ paper: classes.drawerPaper }}
            variant="permanent"
            anchor="left"
            className={classes.drawer}
        >
            {drawerInner}
        </Drawer>
    );
};


const drawerWidthExpanded = 336;
const drawerWidthCollapsed = 84;
const mobileDrawerWidth = 280;

const useStyles = makeStyles((theme: Theme) => ({
    drawer: {
        width: (props: any) => props.isOpen ? drawerWidthExpanded : drawerWidthCollapsed,
        flexShrink: 0,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    drawerPaper: {
        width: (props: any) => props.isMobile ? mobileDrawerWidth : (props.isOpen ? drawerWidthExpanded : drawerWidthCollapsed),
        backgroundColor: (props: any) => props.isDark ? '#0f172a' : theme.palette.background.paper,
        zIndex: 2,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRight: (props: any) => props.isDark ? 'none' : `1px solid ${theme.palette.divider}`,
        boxShadow: (props: any) => props.isDark
            ? '4px 0 24px rgba(0, 0, 0, 0.25)'
            : '2px 0 12px rgba(0, 0, 0, 0.06)',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: (props: any) => (props.isOpen || props.isMobile) ? 'flex-start' : 'center',
        padding: '20px',
        minHeight: '80px',
        flexShrink: 0,
        borderBottom: (props: any) => props.isDark
            ? '1px solid rgba(255, 255, 255, 0.10)'
            : `1px solid ${theme.palette.divider}`,
    },
    // Desktop collapse toggle — floats at the drawer edge
    menuButton: {
        position: 'fixed',
        left: (props: any) => props.isOpen ? drawerWidthExpanded : drawerWidthCollapsed,
        top: '45px',
        transform: 'translate(-50%, -50%)',
        transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 3,
        backgroundColor: (props: any) => props.isDark ? '#1e293b' : theme.palette.background.subtle,
        border: (props: any) => props.isDark
            ? '1px solid rgba(255, 255, 255, 0.12)'
            : `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        padding: '8px',
        color: (props: any) => props.isDark ? '#94a3b8' : theme.palette.text.hint,
        '&:hover': {
            backgroundColor: (props: any) => props.isDark ? '#334155' : theme.palette.background.default,
            color: (props: any) => props.isDark ? '#e2e8f0' : theme.palette.text.primary,
        },
    },
    // Mobile close button — sits inline inside the drawer header
    closeButton: {
        marginLeft: 'auto',
        color: (props: any) => props.isDark ? '#94a3b8' : theme.palette.text.hint,
        '&:hover': {
            backgroundColor: (props: any) => props.isDark ? 'rgba(255,255,255,0.08)' : theme.palette.background.default,
        },
    },
    // Mobile hamburger — fixed at top-left of the page
    hamburger: {
        position: 'fixed',
        top: 12,
        left: 12,
        zIndex: 1300,
        backgroundColor: (props: any) => props.isDark ? '#1e293b' : theme.palette.background.paper,
        border: (props: any) => props.isDark
            ? '1px solid rgba(255, 255, 255, 0.12)'
            : `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        padding: '8px',
        color: (props: any) => props.isDark ? '#94a3b8' : theme.palette.text.hint,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        '&:hover': {
            backgroundColor: (props: any) => props.isDark ? '#334155' : theme.palette.background.default,
        },
    },
    logoWrapper: {
        width: (props: any) => (props.isOpen || props.isMobile) ? 200 : 47,
        overflow: 'hidden',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.3s ease',
        flexShrink: 0,
        marginLeft: (props: any) => (props.isOpen || props.isMobile) ? '20px' : '0',
        marginRight: (props: any) => (props.isOpen || props.isMobile) ? '8px' : '0',
    },
    logo: {
        display: 'block',
        width: 200,
        minWidth: 200,
        filter: (props: any) => props.isDark ? 'brightness(0) invert(1)' : 'none',
        transition: 'filter 0.25s ease',
    },
}));

const LinkList = styled('ul')({
    listStyleType: 'none',
    listStylePosition: 'inside',
    padding: 0,
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
});

const NavbarLink = ({
    path,
    iconSrc,
    iconOverride,
    exact,
    altText,
    linkText,
    authRoles,
    isActive,
    isDrawerOpen,
    isDark,
}: {
    exact?: boolean;
    path: string;
    iconSrc: { inactive: string; active?: string };
    iconOverride?: { inactive: JSX.Element; active: JSX.Element };
    altText: string;
    linkText: string;
    authRoles?: UserLevel[];
    isActive: boolean;
    isDrawerOpen: boolean;
    isDark: boolean;
}) => {
    const { roles } = useAuth();
    const userIsAuthorized = useMemo(
        () => (authRoles ? roles.some((r) => authRoles.indexOf(r as UserLevel) !== -1) : true),
        [authRoles, roles]
    );
    if (!userIsAuthorized) {
        return null;
    }
    return (
        <NavLinkContainer isActive={isActive} isDark={isDark}>
            <Tooltip title={!isDrawerOpen ? linkText : ''} placement="right">
                <span style={{ display: 'block' }}>
                    <NavLinkStyled to={path} exact={exact} isDrawerOpen={isDrawerOpen} isDark={isDark}>
                        <div>
                            {iconOverride ? (
                                isActive ? iconOverride.active : iconOverride.inactive
                            ) : (
                                <img src={iconSrc.inactive} alt={altText} />
                            )}
                            {isDrawerOpen && <span>{linkText}</span>}
                        </div>
                    </NavLinkStyled>
                </span>
            </Tooltip>
        </NavLinkContainer>
    );
};

interface NavLinkContainerProps {
    isActive: boolean;
    isDark: boolean;
    children?: any;
    className?: string;
}

const NavLinkContainer = styled(({ isActive, isDark: _isDark, children, className }: NavLinkContainerProps) => (
    <li className={className}>{children}</li>
))(({ theme, isActive, isDark }: NavLinkContainerProps & { theme: Theme }) => ({
    borderLeft: isActive
        ? `7px solid ${isDark ? '#4361ee' : theme.palette.primary.light}`
        : '7px solid transparent',
    backgroundColor: isActive
        ? (isDark ? 'rgba(67, 97, 238, 0.28)' : 'rgba(242, 248, 255)')
        : 'inherit',
    color: isActive
        ? (isDark ? '#ffffff' : theme.palette.text.hint)
        : (isDark ? '#c5d0e8' : theme.palette.text.hint),
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
        backgroundColor: isActive
            ? (isDark ? 'rgba(67, 97, 238, 0.35)' : theme.palette.background.subtle)
            : (isDark ? 'rgba(255, 255, 255, 0.06)' : theme.palette.background.subtle),
        color: isDark ? '#ffffff' : theme.palette.text.primary,
    },
}));

const NavLinkBase = ({ isDrawerOpen: _isDrawerOpen, isDark: _isDark, ...rest }: React.ComponentProps<typeof StyledLink> & { isDrawerOpen: boolean; isDark: boolean }) =>
    <StyledLink {...rest} />;

const NavLinkStyled = styled(NavLinkBase)(({ theme, isDrawerOpen, isDark }: { theme: Theme; isDrawerOpen: boolean; isDark: boolean }) => ({
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    letterSpacing: '0.01em',
    padding: '22px 0',
    width: '100%',
    color: 'inherit',
    textDecoration: 'none',

    '& div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: isDrawerOpen ? 'flex-start' : 'center',
        width: isDrawerOpen ? '200px' : 'auto',
    },
    '& img': {
        width: '16px',
        height: '16px',
        objectFit: 'contain',
        marginRight: isDrawerOpen ? '16px' : '0',
        flexShrink: 0,
        filter: isDark ? 'brightness(0) invert(1)' : 'none',
        opacity: isDark ? 0.7 : 1,
        transition: 'filter 0.25s ease, opacity 0.25s ease',
    },
    '& svg': {
        marginRight: isDrawerOpen ? '16px' : '0',
        fontSize: '20px',
        flexShrink: 0,
        color: isDark ? 'inherit' : theme.palette.text.disabled,
    },
    '& span': {
        whiteSpace: 'nowrap',
    },
}));

const DarkModeToggle = ({
    isDark,
    isDrawerOpen,
    onToggle,
}: {
    isDark: boolean;
    isDrawerOpen: boolean;
    onToggle: () => void;
}) => {
    const theme = useTheme();
    const borderColor = isDark ? 'rgba(255, 255, 255, 0.06)' : theme.palette.divider;
    const textColor = isDark ? '#6b7280' : theme.palette.text.disabled;
    const iconColor = isDark ? '#6b7280' : theme.palette.text.disabled;
    const hoverBg = isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.subtle;

    return (
        <Tooltip title={!isDrawerOpen ? (isDark ? 'Switch to Light' : 'Switch to Dark') : ''} placement="right">
            <div
                onClick={onToggle}
                style={{
                    borderTop: `1px solid ${borderColor}`,
                    padding: isDrawerOpen ? '14px 20px' : '14px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isDrawerOpen ? 'flex-start' : 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'background-color 0.15s ease',
                    userSelect: 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = hoverBg)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: isDrawerOpen ? '20px' : '0',
                }}>
                    {isDark
                        ? <Brightness7 style={{ color: iconColor, fontSize: 20 }} />
                        : <Brightness4 style={{ color: iconColor, fontSize: 20 }} />
                    }
                </span>
                {isDrawerOpen && (
                    <span style={{ fontSize: '13px', color: textColor, fontWeight: 500 }}>
                        {isDark ? 'Light Mode' : 'Dark Mode'}
                    </span>
                )}
            </div>
        </Tooltip>
    );
};
